import React, { useState, useContext, useEffect } from 'react';
import { Button, Card, Col, Modal, Row, Spin, Tooltip } from 'antd';
import TimeSeriesMenu from './TimeSeriesMenu';
import errorChecker from 'utils/errorChecker';
import homeService from 'services/homeService';
import fileRecognize from 'utils/fileType';
import dayjs from 'dayjs';
import { useNotificationHandler } from 'hooks/useNotificationHandler';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import L from 'leaflet';

import "assets/styles/Home/HomeUI/TimeSeries/time-series-render.css";
import useActiveLayersStore from 'stores/useSelectedLayersStore';
import useLeafletLayerManager from 'hooks/useLeafletLayerManager';
import LayersAndResolutions from 'data/layers/LayersAndResolutions.json';
import useSearchParamsManager from 'hooks/useSearchParamsManager';
import { sortByDateAsc } from 'utils/dateFunctions';

const API_URL = process.env.REACT_APP_API_URL;

function TimeSeriesRender({
    mapRef,
    setIsTSRButtonClicked,
    setErrorMessage,
    setStartDate,
    layerRefs, setIsDatePickerDisabled, isDatePickerDisabled, setEnabledDates, fetchCommonDates, setMapParameters
}) {
    const [startDate, setLocalStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedResolutions, setSelectedResolutions] = useState({});
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [imageData, setImageData] = useState([]);
    const [selectedImages, setSelectedImages] = useState([]);
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [cachedImageBlobs, setCachedImageBlobs] = useState({});
    const { addLeafletLayer, createLeafletLayer, clearLeafletLayers } = useLeafletLayerManager();
    const updateAllLayers = useActiveLayersStore((state) => state.updateAllLayers);

    const {
        notifySuccess,
        notifyError,
        notifyInfo,
        notifyLoading,
        closeNotification,
        contextHolder,
        openNotification,
        handleError
    } = useNotificationHandler();
    const { replaceUrlParams } = useSearchParamsManager();

    // this caches images when they're first loaded
    useEffect(() => {
        if (imageData.length > 0) {
            imageData.forEach(img => fetchAndCacheImage(img.url));
        }
    }, [imageData]);

    const fetchAndCacheImage = async (url) => {
        if (cachedImageBlobs[url]) return; //skips already cached images

        try {
            const response = await fetch(url);
            if (!response.ok) return;

            const blob = await response.blob();
            setCachedImageBlobs(prev => ({
                ...prev,
                [url]: blob
            }));
        } catch (error) {
            console.error(`Failed to cache image ${url}:`, error);
        }
    };

    const handleClose = () => {
        setIsTSRButtonClicked(prev => !prev);
    };

    const handleGoToDate = async (imageMetadata) => {
        try {
            const loadingKey = notifyLoading(
                'Updating Map',
                `Loading ${imageMetadata.layer} for ${dayjs(imageMetadata.date).format('DD/MM/YYYY')}`,
                0
            );

            const dateObj = dayjs(imageMetadata.date);
            updateAllLayers([imageMetadata.spaceResolution], [imageMetadata.timeResolution], [imageMetadata.layer]);
            // Extract selectedDay, selectedMonth, selectedYear
            const selectedDay = imageMetadata.date.split('-')[2];
            const selectedMonth = imageMetadata.date.split('-')[1];
            const selectedYear = imageMetadata.date.split('-')[0];


            const providerKeyLower = imageMetadata.provider.toLowerCase();
            const layerKeyLower = imageMetadata.layer.toLowerCase();

            const layerInfo = LayersAndResolutions.layers.find(l => {
                const shortName = l.shortName?.toLowerCase();
                const longName = l.longName?.toLowerCase();
                const fileName = l.fileName?.toLowerCase();
                return (
                    shortName === providerKeyLower ||
                    longName === providerKeyLower ||
                    fileName === providerKeyLower ||
                    shortName === layerKeyLower ||
                    longName === layerKeyLower ||
                    fileName === layerKeyLower
                );
            });

            const layerParameters = {
                fileName: layerInfo?.fileName || imageMetadata.layer,
                provider: layerInfo?.shortName || imageMetadata.provider,
                longName: layerInfo?.longName || imageMetadata.layer.toUpperCase(),
                shortName: layerInfo?.shortName || imageMetadata.layer.toLowerCase(),
                spaceResolution: imageMetadata.spaceResolution,
                timeResolution: imageMetadata.timeResolution,
                predictionDay: 0,
            };
            clearLeafletLayers(mapRef, layerRefs);
            const newLayer = await createLeafletLayer(layerParameters, selectedDay, selectedMonth, selectedYear, 50, openNotification, handleError);

            addLeafletLayer(newLayer, mapRef, layerRefs);
            setIsSelectionMode(false);
            setSelectedImages([]);
            setIsDatePickerDisabled(false);
            replaceUrlParams({
                startDate: dayjs(imageMetadata.date),
                layers: [
                    { longName: layerInfo?.longName, spaceResolution: imageMetadata.spaceResolution, timeResolution: imageMetadata.timeResolution }
                ]
            });
            setMapParameters([layerParameters])
            const commonDates = await fetchCommonDates([layerParameters]);
            const orderedDates = sortByDateAsc(commonDates);
            setEnabledDates(orderedDates);

            setTimeout(() => {
                setStartDate(dateObj);
                closeNotification(loadingKey);
                setIsModalVisible(false);
            }, 100);

            setIsTSRButtonClicked(prev => !prev);

        } catch (error) {
            console.error('Error updating map:', error);
            notifyError(
                'Update Failed',
                'Failed to update the map. Please try again.'
            );
        }
    };

    async function submitQueryRender(startDate, endDate, selectedResolutions, mapRef, bbox) {
        setImageData([]);
        setLoading(true);
        setCachedImageBlobs({});
        setIsSelectionMode(false);
        setSelectedImages([]);

        const loadingKey = notifyLoading(
            'Loading Images',
            'Please wait while the images are being loaded. Sit back and relax.',
            0
        );


        const bbox_3857 = {
            southWest: L.CRS.EPSG3857.project(bbox.southWest),
            northEast: L.CRS.EPSG3857.project(bbox.northEast)
        };

        const hasErrors = errorChecker(setErrorMessage, setLoading, startDate, endDate, selectedResolutions);
        if (hasErrors) {
            closeNotification(loadingKey);
            notifyError('Error', 'Please check the selected dates and resolutions.');
            setLoading(false);
            return;
        }

        const layer = Object.keys(selectedResolutions)[0];
        const spaceResolution = selectedResolutions[layer].space;
        const timeResolution = selectedResolutions[layer].time;

        try {
            const file = fileRecognize(layer);
            const fileName = file.fileName;
            const provider = file.provider;

            const returnedData = await homeService.getRender(
                spaceResolution,
                timeResolution,
                startDate,
                endDate,
                provider,
                mapRef,
                bbox_3857,
                fileName
            );

            if (returnedData && returnedData.imageUrls) {
                const processedImageData = returnedData.imageUrls.map(url => {
                    const filename = url.split('/').pop();
                    const dateMatch = filename.match(/map_(\d{4})-(\d{2})-(\d{2})/);

                    let date = null;

                    if (dateMatch) {
                        const [_, year, month, day] = dateMatch;
                        date = `${year}-${month}-${day}`;
                    }

                    return {
                        url: API_URL + url,
                        date: date,
                        layer: layer,
                        spaceResolution: spaceResolution,
                        timeResolution: timeResolution,
                        provider: provider
                    };
                });

                setImageData(processedImageData);
                setIsModalVisible(true);

                closeNotification(loadingKey);
                notifySuccess('Images Loaded', 'Time Series Images completed loading.');
            } else {
                throw new Error('Image URLs are missing from the response');
            }
        } catch (error) {
            closeNotification(loadingKey);
            notifyError(
                'Error',
                `There was a problem loading images. Error fetching dates for ${layer}`
            );
            console.error(`Error fetching dates for ${layer}:`, error);
        } finally {
            setLoading(false);
        }
    }

    const handleImageSelection = (imageUrl) => {
        setSelectedImages(prev => {
            if (prev.includes(imageUrl)) {
                return prev.filter(url => url !== imageUrl);
            } else {
                return [...prev, imageUrl];
            }
        });
    };

    const handleSelect = () => {
        setIsSelectionMode(prev => {
            if (prev === true) {
                setSelectedImages([]);
            }
            return !prev;
        });
    };

    const handleSelectAll = () => {
        if (selectedImages.length === imageData.length) {
            // If all are selected, deselect all
            setSelectedImages([]);
        } else {
            // Otherwise select all
            setSelectedImages(imageData.map(img => img.url));
        }
    };

    const handleDownload = async () => {
        const imagesToDownload = selectedImages.length > 0
            ? selectedImages
            : imageData.map(img => img.url);

        // If only one image, it downlaods it as PNG file or whatever file they are stored in
        if (imagesToDownload.length === 1) {
            const url = imagesToDownload[0];
            const imgData = imageData.find(img => img.url === url);
            if (!imgData) return;

            const blob = cachedImageBlobs[url];
            if (!blob) {
                notifyError(
                    'Download Failed',
                    'Image not found in cache. Try reloading the page.'
                );
                return;
            }

            const objectUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = objectUrl;

            const formattedDate = imgData.date ? imgData.date : 'unknown-date';
            const downloadName = `${imgData.layer}-${imgData.spaceResolution}-${imgData.timeResolution}-${formattedDate}.png`;

            a.download = downloadName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            setTimeout(() => URL.revokeObjectURL(objectUrl), 100);

            notifySuccess(
                'Download Complete',
                'Image downloaded successfully.'
            );
            return;
        }

        // for two or more files we will download them as a zip file (format is the same)
        try {
            const loadingKey = notifyLoading(
                'Creating ZIP',
                'Preparing your images for download...',
                0
            );

            const zip = new JSZip();
            const imageFolder = zip.folder("timeseries-images");

            let successCount = 0;
            let errorCount = 0;

            // each image goes into zip
            for (const url of imagesToDownload) {
                const imgData = imageData.find(img => img.url === url);
                if (!imgData) continue;

                const blob = cachedImageBlobs[url];
                if (!blob) {
                    errorCount++;
                    continue;
                }

                const formattedDate = imgData.date ? imgData.date : 'unknown-date';
                const fileName = `${imgData.layer}-${imgData.spaceResolution}-${imgData.timeResolution}-${formattedDate}.png`;

                imageFolder.file(fileName, blob);
                successCount++;
            }

            closeNotification(loadingKey);

            if (successCount === 0) {
                notifyError(
                    'Download Failed',
                    'No images available to download. Try reloading the page.'
                );
                return;
            }

            // zip is generated
            const content = await zip.generateAsync({ type: "blob" });
            // atm it is saved as timeseries-images-[the date you downloaded].zip I think it should stay like this unless 
            // there is a better way to name it
            const timestamp = new Date().toISOString().split('T')[0];
            saveAs(content, `timeseries-images-${timestamp}.zip`);

            notifySuccess(
                'Download Complete',
                `Successfully downloaded ${successCount} images as a ZIP file.`
            );

            if (errorCount > 0) {
                notifyInfo(
                    'Note',
                    `${errorCount} images could not be included in the ZIP file.`
                );
            }
        } catch (error) {
            console.error('Error creating ZIP file:', error);
            notifyError(
                'Download Failed',
                'There was a problem creating the ZIP file. Try downloading images individually.'
            );
        }
    };

    return (
        <div className='tsr-container'>
            {contextHolder}

            <TimeSeriesMenu
                type={'render'}
                selectedResolutions={selectedResolutions}
                setSelectedResolutions={setSelectedResolutions}
                setErrorMessage={setErrorMessage}
                startDate={startDate}
                setStartDate={setLocalStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                loading={loading}
                submitQuery={submitQueryRender}
                setIsButtonClicked={(value) => setIsTSRButtonClicked(prev => !prev)}
                setLoading={setLoading}
                bounds={mapRef.current.getBounds()}
                mapRef={mapRef}
                handleFunction={handleClose}
            />

            <Modal
                title="Time Series Images"
                open={isModalVisible}
                onCancel={() => {
                    setIsModalVisible(false);
                    setIsSelectionMode(false);
                    setSelectedImages([]);
                }}
                footer={null}
                centered
                width="90%"
                styles={{
                    body: {
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                    }
                }}
                zIndex={1500}
            >
                {loading ? (
                    <div style={{ textAlign: 'center' }}>
                        <Spin size="large" />
                    </div>
                ) : (
                    <div className={`tsr ${isSelectionMode ? 'selection-mode' : ''}`}>
                        <div className="tsr-controls">
                            <Button
                                type={isSelectionMode ? "primary" : "default"}
                                onClick={handleSelect}
                            >
                                {isSelectionMode ? "Done Selecting" : "Select Images"}
                            </Button>
                            <Button
                                onClick={handleSelectAll}
                                disabled={!isSelectionMode}
                                hidden={!isSelectionMode}
                            >
                                {selectedImages.length === imageData.length ? "Deselect All" : "Select All"}
                            </Button>
                            <Button
                                type="primary"
                                onClick={handleDownload}
                            >
                                <i className="fa-solid fa-download"></i> Download {selectedImages.length > 0 ? `(${selectedImages.length})` : 'All'}
                            </Button>
                        </div>
                        <div className="image-gallery">
                            {imageData.map((imgData, index) => (
                                <div
                                    className={`image-card ${selectedImages.includes(imgData.url) ? 'selected' : ''}`}
                                    key={index}
                                    onClick={(e) => {
                                        if (isSelectionMode) {
                                            handleImageSelection(imgData.url);
                                        } else if (e.target.className.includes('image-card') || e.target.className.includes('ant-card')) {
                                            handleGoToDate(imgData);
                                        }
                                    }}
                                >
                                    {isSelectionMode ? (
                                        <Card
                                            hoverable={false}
                                            cover={
                                                <img
                                                    src={imgData.url}
                                                    alt={`From ${imgData.date}`}
                                                    onLoad={() => fetchAndCacheImage(imgData.url)}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleImageSelection(imgData.url);
                                                    }}
                                                />
                                            }
                                        >
                                            <div className="image-metadata">
                                                <p><b>Date:</b> {imgData.date ? dayjs(imgData.date).format('DD/MM/YYYY') : 'Date not available'}</p>
                                            </div>
                                        </Card>
                                    ) : (
                                        <Tooltip title="Load layer's date">
                                            <Card
                                                hoverable={true}
                                                cover={
                                                    <img
                                                        src={imgData.url}
                                                        alt={`From ${imgData.date}`}
                                                        onLoad={() => fetchAndCacheImage(imgData.url)}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleGoToDate(imgData);
                                                        }}
                                                    />
                                                }
                                            >
                                                <div className="image-metadata">
                                                    <p><b>Date:</b> {imgData.date ? dayjs(imgData.date).format('DD/MM/YYYY') : 'Date not available'}</p>
                                                </div>
                                            </Card>
                                        </Tooltip>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}

export default TimeSeriesRender;